/**
 * Direct public-client intake. Mirrored in the desktop repo; no private key.
 *
 * Delivery semantics (at-least-once, duplicates are filterable):
 *   - A non-2xx intake response is definitely-not-accepted. The batch stays
 *     queued and is retried with backoff, unchanged.
 *   - A THROWN post (AbortSignal.timeout, "Failed to fetch" after a keepalive
 *     hand-off) is ambiguous: the intake may already hold the batch. Datadog
 *     does not dedupe on event_id, so the retry is still allowed — dropping
 *     the batch would lose real errors — but every entry re-sent after an
 *     ambiguous failure carries delivery_attempt (2, 3, ...) and
 *     redelivery:true. Query with count_distinct(@event_id) or
 *     -@redelivery:true to see each event once.
 */
import { isSensitiveKey, redactSecretsInString } from "./log-redaction"

type Attributes = Record<string, unknown>
type Level = "info" | "warn" | "error"
type Entry = {
  level: Level
  message: string
  attributes: Attributes
  eventId?: string
  createdAt?: number
  // Number of intake posts this entry has been handed to. Only a thrown post
  // bumps it; a rejected (non-2xx) post never delivered anything.
  attempt?: number
}
type Context = {
  clientToken: string | null
  site: string
  env?: string
  version?: string
  user?: { id: string; email: string | null } | null
  request_ip?: string
  request_geo_country?: string
  request_geo_region?: string
  request_geo_city?: string
}
const INTAKES: Record<string, string> = {
  "datadoghq.com": "https://browser-intake-datadoghq.com",
  "datadoghq.eu": "https://browser-intake-datadoghq.eu",
  "us3.datadoghq.com": "https://browser-intake-us3-datadoghq.com",
  "us5.datadoghq.com": "https://browser-intake-us5-datadoghq.com",
  "ap1.datadoghq.com": "https://browser-intake-ap1-datadoghq.com",
  "ap2.datadoghq.com": "https://browser-intake-ap2-datadoghq.com",
}

function scrubString(value: string): string {
  return redactSecretsInString(value).slice(0, 4000)
}

const RESERVED_FIELDS = new Set(["status", "status_detail", "http_status_code", "user_id", "user_email", "app_version", "source", "process", "service", "session_id", "anonymous_id", "visitor_id", "device_id", "launch_id", "correlation_id", "trace_id", "event_name", "event", "page_path", "pathname"])

export function sanitizeDirectLog(value: unknown, depth = 0): unknown {
  if (depth > 6) return "[truncated]"
  if (typeof value === "string") return scrubString(value)
  if (Array.isArray(value))
    return value.slice(0, 30).map((item) => sanitizeDirectLog(item, depth + 1))
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value)
        .sort(([left], [right]) => Number(RESERVED_FIELDS.has(right)) - Number(RESERVED_FIELDS.has(left)))
        .slice(0, 100)
        .filter(
          ([key]) => !isSensitiveKey(key.replace(/-/g, "_")) && !/signature|key_value/i.test(key)
        )
        .map(([key, item]) => [key, sanitizeDirectLog(item, depth + 1)])
    )
  }
  return value
}

export function directIntakeUrl(context: Context): string | null {
  const origin = INTAKES[context.site]
  if (!origin || !context.clientToken) return null
  const query = new URLSearchParams({
    "dd-api-key": context.clientToken,
    ddsource: "browser",
    "dd-evp-origin": "browser",
  })
  return `${origin}/api/v2/logs?${query}`
}

export function buildDirectLog(entry: Entry, context: Context, runtime: "desktop" | "browser") {
  return buildSanitizedLog({ ...entry, message: scrubString(entry.message),
    attributes: sanitizeDirectLog(entry.attributes) as Attributes }, context, runtime)
}

function buildSanitizedLog(entry: Entry, context: Context, runtime: "desktop" | "browser") {
  const attributes = entry.attributes
  const claimedId =
    typeof attributes.user_id === "string" && attributes.user_id ? attributes.user_id : null
  // Context is obtained from the application's authenticated endpoint. On a
  // direct public-token log it is still client-origin data, never proof of auth.
  const user = context.user && (claimedId === context.user.id || (runtime === "browser" && !claimedId)) ? context.user : null
  const id = user?.id || claimedId || "anonymous"
  const email =
    user?.email ||
    (claimedId && typeof attributes.user_email === "string" ? attributes.user_email : null) ||
    "unknown"
  const service = `phantomcode-${runtime}`
  const country = context.request_geo_country || "unknown"
  const region = context.request_geo_region || "unknown"
  const city = context.request_geo_city || "unknown"
  const attempt = entry.attempt ?? 1
  return {
    ...attributes,
    message: entry.message,
    // `status` is Datadog's severity slot, so the caller's value is moved
    // aside rather than dropped: an HTTP code is indexed twice (status_detail
    // plus http_status_code), any other value (a state string such as
    // "failed", a boolean) survives as status_detail text.
    ...buildStatusDetail(attributes.status),
    status: entry.level,
    event_id: entry.eventId ?? crypto.randomUUID(),
    date: entry.createdAt ?? Date.now(),
    ...(attempt > 1 ? { delivery_attempt: attempt, redelivery: true } : {}),
    app: "phantomcode",
    runtime,
    service,
    source: runtime === "desktop" ? "desktop" : "browser",
    ddsource: "browser",
    version:
      runtime === "desktop"
        ? String(attributes.app_version || "unknown")
        : context.version || "unknown",
    ...(runtime === "desktop" ? { app_version: attributes.app_version || "unknown" } : {}),
    env: context.env || "production",
    ddtags: `app:phantomcode,runtime:${runtime},service:${service},env:${context.env || "production"}`,
    user_id: id,
    user_email: email,
    visitor_id:
      id !== "anonymous"
        ? `user:${id}`
        : `anonymous:${attributes.anonymous_id || attributes.session_id || "unknown"}`,
    usr: { id, email },
    user_id_verified: false,
    identity_source: id === "anonymous" ? "none" : "client_session",
    request_ip: context.request_ip || "unknown",
    request_geo_country: country,
    request_geo_region: region,
    request_geo_city: city,
    geo_source: context.request_ip ? "vercel_edge_bootstrap" : "unknown",
    network: {
      client: {
        ip: context.request_ip || "unknown",
        geoip: {
          country: { iso_code: country },
          subdivision: { iso_code: region },
          city: { name: city },
        },
      },
    },
  }
}

function buildStatusDetail(status: unknown): { status_detail?: number | string; http_status_code?: number } {
  if (typeof status === "number")
    return status >= 100 && status <= 599 ? { status_detail: status, http_status_code: status } : {}
  if (status === undefined || status === null) return {}
  return { status_detail: typeof status === "object" ? JSON.stringify(status) : String(status) }
}

export function createDirectDatadogTransport(options: {
  webappUrl: string
  runtime: "desktop" | "browser"
  getAccessToken?: () => string | null
}) {
  let context: Context | null = null
  let loadedAt = 0
  let contextIdentity = ""
  let pending: Promise<void> | null = null
  let timer: ReturnType<typeof setTimeout> | null = null
  const queue: Entry[] = []
  let retries = 0
  let retryAt = 0

  async function flush() {
    if (pending || queue.length === 0) return pending
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      schedule(30_000)
      return
    }
    if (Date.now() < retryAt) {
      schedule(retryAt - Date.now())
      return
    }
    if (timer !== null) clearTimeout(timer)
    timer = null
    pending = (async () => {
      const identity = String(queue[0].attributes.user_id || "anonymous")
      try {
        if (!context || Date.now() - loadedAt > 300_000 || contextIdentity !== identity) {
          const token = options.getAccessToken?.()
          const response = await fetch(`${options.webappUrl}/api/telemetry-context`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            credentials: options.runtime === "browser" ? "same-origin" : "omit",
            cache: "no-store",
            signal: AbortSignal.timeout(5000),
          })
          if (!response.ok) throw new Error("Telemetry configuration unavailable")
          context = (await response.json()) as Context
          loadedAt = Date.now()
          contextIdentity = identity
        }
        const url = directIntakeUrl(context!)
        if (!url) throw new Error("Client intake is not configured")
        // Small batches fit keepalive's 64 KiB budget. Retain unsent events on
        // network/429/5xx failures; no fallback forwarding hop is introduced.
        const entries: Entry[] = []
        const lines: string[] = []
        let bytes = 2
        for (const entry of queue.slice(0, 20)) {
          if (String(entry.attributes.user_id || "anonymous") !== identity) break
          const line = JSON.stringify(buildSanitizedLog(entry, context!, options.runtime))
          const size = new TextEncoder().encode(line).byteLength
          if (bytes + size > 55_000 && entries.length) break
          entries.push(entry)
          lines.push(line)
          bytes += size + 1
        }
        let response: Response
        try {
          response = await fetch(url, {
            method: "POST",
            headers: { "Content-Type": "text/plain" },
            // Browser intake uses one JSON object per line. A JSON array is
            // accepted as a single plain-text message and loses indexed fields.
            body: lines.join("\n"),
            keepalive: bytes < 60_000,
            signal: AbortSignal.timeout(5000),
          })
        } catch (error) {
          // The post may have landed before the timeout or the keepalive
          // hand-off severed the response; mark the batch so the retry is
          // recognisable as a possible duplicate (see file header).
          for (const entry of entries) entry.attempt = (entry.attempt ?? 1) + 1
          throw error
        }
        if (!response.ok) throw new Error("Datadog delivery failed")
        // Overflow may have evicted an in-flight entry. Remove only the exact
        // delivered objects, never unsent entries that shifted into their place.
        const sent = new Set(entries)
        for (let i = queue.length - 1; i >= 0; i--) if (sent.has(queue[i])) queue.splice(i, 1)
        retries = 0
        retryAt = 0
      } catch {
        retries = Math.min(retries + 1, 6)
        retryAt = Date.now() + Math.min(1000 * 2 ** retries, 60_000)
      }
    })().finally(() => {
      pending = null
      if (queue.length) schedule(Math.max(0, retryAt - Date.now()))
    })
    return pending
  }

  function schedule(delay: number) {
    if (timer) return
    timer = setTimeout(() => {
      timer = null
      void flush()
    }, delay)
    // A delivery retry must not keep the Electron main process alive at exit.
    ;(timer as unknown as { unref?: () => void }).unref?.()
  }

  return {
    send(level: Level, message: string, attributes: Attributes = {}) {
      // Snapshot context and sanitize before retaining anything in memory.
      queue.push({
        eventId: crypto.randomUUID(),
        createdAt: Date.now(),
        level,
        message: scrubString(message),
        attributes: sanitizeDirectLog(attributes) as Attributes,
      })
      if (queue.length > 500) queue.shift()
      if (level !== "info" || queue.length >= 20) void flush()
      else schedule(500)
    },
    flush,
    invalidateContext() {
      context = null
      loadedAt = 0
    },
    flushOnUnload() {
      if (pending) return
      const url = context && directIntakeUrl(context)
      if (!url || !queue.length || typeof navigator === "undefined" || !navigator.sendBeacon) {
        void flush()
        return
      }
      const entries: Entry[] = []
      for (const entry of queue.slice(0, 20)) {
        if (String(entry.attributes.user_id || "anonymous") !== contextIdentity) break
        entries.push(entry)
      }
      if (!entries.length) {
        void flush()
        return
      }
      const body = entries
        .map((entry) => JSON.stringify(buildSanitizedLog(entry, context!, options.runtime)))
        .join("\n")
      if (
        new TextEncoder().encode(body).byteLength < 60_000 &&
        navigator.sendBeacon(url, new Blob([body], { type: "text/plain" }))
      )
        queue.splice(0, entries.length)
    },
  }
}
